import { Button, GoogleLogo, MicrosoftLogo, YahooLogo } from '@approvalmax/ui';
import { miscHelpers, routerHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { signup } from 'pages/auth/actions';
import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { amplitudeService } from 'services/amplitude';
import { routingService } from 'services/routing';
import { useResendTimeout } from 'shared/hooks/useResendTimeout';
import { getPath, Path } from 'urlBuilder';

import { resendTimeout, resendTimeoutStorageKey } from './EmailConfirmation.constants';
import { messages } from './EmailConfirmation.messages';
import {
    BigText,
    ChangeEmailButton,
    EmailText,
    FindEmail,
    FindEmailTitle,
    OpenEmailContainer,
    OpenEmailLink,
    ProgressContainer,
    Root,
} from './EmailConfirmation.styles';

const EmailConfirmation = memo(() => {
    const search = useLocation().search;
    const email = routerHelpers.getParamAsString(search, 'email', '');
    const initialApp = routerHelpers.getParamAsString(search, 'app', '');
    const app = miscHelpers.castToEnum(domain.SsoLoginApplication, initialApp);
    const consentMarketingAccepted = routerHelpers.getParamAsString(location.search, 'consentMarketingAccepted', '');

    const dispatch = useDispatch();

    const { startTimer, isTimerActive, formattedResendTime } = useResendTimeout(resendTimeoutStorageKey, resendTimeout);

    const handleResend = () => {
        amplitudeService.sendData('signup: click on resend button');

        dispatch(signup(email, Boolean(consentMarketingAccepted), app));
        startTimer();
    };

    const handleChangeEmail = useCallback(() => {
        amplitudeService.sendData('signup: click on change email');

        routingService.push(
            getPath(Path.register, {
                email,
                app,
            })
        );
    }, [app, email]);

    return (
        <Root>
            <ProgressContainer currentStep={1} />

            <BigText>{messages.linkSent}</BigText>

            <EmailText>{email}</EmailText>

            <BigText>{messages.clickToVerify}</BigText>

            <ChangeEmailButton colorTheme='grey' preset='small' execute={handleChangeEmail}>
                {messages.changeEmail}
            </ChangeEmailButton>

            <OpenEmailContainer>
                <OpenEmailLink
                    href='https://mail.google.com'
                    target='_blank'
                    rel='noreferrer noopener'
                    onClick={() =>
                        amplitudeService.sendData('signup: click on email shortcut', {
                            type: 'gmail',
                        })
                    }
                >
                    <GoogleLogo />

                    {messages.openGmail}
                </OpenEmailLink>

                <OpenEmailLink
                    href='https://mail.yahoo.com'
                    target='_blank'
                    rel='noreferrer noopener'
                    onClick={() =>
                        amplitudeService.sendData('signup: click on email shortcut', {
                            type: 'yahoo',
                        })
                    }
                >
                    <YahooLogo />

                    {messages.openYahoo}
                </OpenEmailLink>

                <OpenEmailLink
                    href='https://outlook.live.com/mail'
                    target='_blank'
                    rel='noreferrer noopener'
                    onClick={() =>
                        amplitudeService.sendData('signup: click on email shortcut', {
                            type: 'outlook',
                        })
                    }
                >
                    <MicrosoftLogo />

                    {messages.openOutlook}
                </OpenEmailLink>
            </OpenEmailContainer>

            <FindEmailTitle>{messages.findEmail}</FindEmailTitle>

            <FindEmail>{messages.checkEmailAndSpam}</FindEmail>

            <Button theme='secondary' execute={handleResend} disabled={isTimerActive}>
                {isTimerActive
                    ? messages.resendIn({
                          time: formattedResendTime,
                      })
                    : messages.resend}
            </Button>
        </Root>
    );
});

EmailConfirmation.displayName = 'EmailConfirmation';

export default EmailConfirmation;
